/**
 * Audit Log Page - P1 Feature
 * Master Admin read-only view of all dealer-related changes
 * 
 * Shows: who changed what, when, and for which dealer
 */
import { useState, useEffect, useCallback } from "react";
import { useAuth } from "../../contexts/AuthContext";
import { useToast } from "../../contexts/ToastContext";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Badge } from "../../components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Skeleton } from "../../components/ui/skeleton";

// Action type labels and colors
const ACTION_CONFIG = {
  dealer_created: { label: "Dealer Created", color: "bg-green-100 text-green-800" },
  dealer_enabled: { label: "Dealer Enabled", color: "bg-blue-100 text-blue-800" },
  dealer_disabled: { label: "Dealer Disabled", color: "bg-orange-100 text-orange-800" },
  dealer_deleted: { label: "Dealer Deleted", color: "bg-red-100 text-red-800" },
  settings_updated: { label: "Settings Updated", color: "bg-purple-100 text-purple-800" },
  invite_created: { label: "Invite Sent", color: "bg-cyan-100 text-cyan-800" },
  invite_accepted: { label: "Invite Accepted", color: "bg-green-100 text-green-800" },
  invite_cancelled: { label: "Invite Cancelled", color: "bg-gray-100 text-gray-800" },
  user_removed: { label: "User Removed", color: "bg-orange-100 text-orange-800" },
  user_hard_deleted: { label: "User Deleted", color: "bg-red-100 text-red-800" },
  reseller_pricing_updated: { label: "Reseller Pricing", color: "bg-emerald-100 text-emerald-800" },
  reseller_created: { label: "Reseller Created", color: "bg-indigo-100 text-indigo-800" },
  reseller_updated: { label: "Reseller Updated", color: "bg-indigo-100 text-indigo-800" },
  login_success: { label: "Login", color: "bg-slate-100 text-slate-700" },
  login_failed: { label: "Login Failed", color: "bg-red-100 text-red-700" },
};

const formatDate = (isoString) => {
  if (!isoString) return "—";
  const date = new Date(isoString);
  return date.toLocaleString("en-US", {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
  });
};

const AuditLogPage = ({ onNavigate }) => {
  const { apiFetch } = useAuth();
  const { error: showError } = useToast();
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [actionFilter, setActionFilter] = useState("all");
  const [dealers, setDealers] = useState([]);
  const [dealerFilter, setDealerFilter] = useState("all");
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const LIMIT = 25;

  // Fetch dealers for filter dropdown
  const fetchDealers = useCallback(async () => {
    try {
      const response = await apiFetch("/admin/dealers");
      setDealers(response.data.dealers || []);
    } catch (err) {
      console.error("Failed to load dealers for filter:", err);
    }
  }, [apiFetch]);

  // Fetch audit logs
  const fetchLogs = useCallback(async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("limit", LIMIT);
      params.append("offset", page * LIMIT);
      if (actionFilter !== "all") params.append("action", actionFilter);
      if (dealerFilter !== "all") params.append("business_id", dealerFilter);
      
      const response = await apiFetch(`/admin/audit-logs?${params.toString()}`);
      setLogs(response.data.logs || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      showError("Failed to load audit logs");
      console.error("Failed to load audit logs:", err);
    } finally {
      setLoading(false);
    }
  }, [apiFetch, actionFilter, dealerFilter, page, showError]);

  useEffect(() => {
    fetchDealers();
  }, [fetchDealers]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  // Reset page when filters change
  useEffect(() => {
    setPage(0);
  }, [actionFilter, dealerFilter]);

  const totalPages = Math.ceil(total / LIMIT);

  const getMetadataDisplay = (log) => {
    if (!log.metadata) return null;
    
    const parts = [];
    
    // Display relevant metadata based on action type
    if (log.metadata.email) {
      parts.push(`Email: ${log.metadata.email}`);
    }
    if (log.metadata.role) {
      parts.push(`Role: ${log.metadata.role}`);
    }
    if (log.metadata.updated_fields && Array.isArray(log.metadata.updated_fields)) {
      parts.push(`Fields: ${log.metadata.updated_fields.join(", ")}`);
    }
    if (log.metadata.name) {
      parts.push(`Name: ${log.metadata.name}`);
    }
    if (log.metadata.dealer_name && !log.dealer_name) {
      parts.push(`Dealer: ${log.metadata.dealer_name}`);
    }
    // Reseller pricing changes
    if (log.action === 'reseller_pricing_updated') {
      if (log.metadata.new_cost !== undefined) {
        parts.push(`Cost: $${log.metadata.old_cost || 0} → $${log.metadata.new_cost}`);
      }
      if (log.metadata.new_retail !== undefined) {
        parts.push(`Retail: $${log.metadata.old_retail || 0} → $${log.metadata.new_retail}`);
      }
    }
    
    return parts.length > 0 ? parts.join(" • ") : null;
  };

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-xl font-semibold">Audit Log</CardTitle>
              <CardDescription className="text-sm text-gray-500 mt-1">
                Track all changes made to dealers and settings
              </CardDescription>
            </div>
            <Button variant="outline" onClick={() => onNavigate?.("admin-dealers")}>
              ← Back to Dealers
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {/* Filters */}
          <div className="flex flex-wrap gap-4 mb-6">
            <div className="w-48">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Action Type</label>
              <Select value={actionFilter} onValueChange={setActionFilter}>
                <SelectTrigger data-testid="action-filter">
                  <SelectValue placeholder="All Actions" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Actions</SelectItem>
                  {Object.entries(ACTION_CONFIG).map(([key, config]) => (
                    <SelectItem key={key} value={key}>{config.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="w-64">
              <label className="text-sm font-medium text-gray-700 mb-1 block">Dealer</label>
              <Select value={dealerFilter} onValueChange={setDealerFilter}>
                <SelectTrigger data-testid="dealer-filter">
                  <SelectValue placeholder="All Dealers" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Dealers</SelectItem>
                  {dealers.map((dealer) => (
                    <SelectItem key={dealer.id} value={dealer.id}>{dealer.name}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex items-end">
              <span className="text-sm text-gray-500">
                {total} total {total === 1 ? "entry" : "entries"}
              </span>
            </div>
          </div>

          {/* Log entries */}
          {loading ? (
            <div className="space-y-3">
              {[...Array(5)].map((_, i) => (
                <Skeleton key={i} className="h-16 w-full" />
              ))}
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              <p>No audit log entries found</p>
              {(actionFilter !== "all" || dealerFilter !== "all") && (
                <p className="text-sm mt-2">Try adjusting your filters</p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              {logs.map((log) => {
                const actionConfig = ACTION_CONFIG[log.action] || { 
                  label: log.action, 
                  color: "bg-gray-100 text-gray-800" 
                };
                const metadataDisplay = getMetadataDisplay(log);
                
                return (
                  <div 
                    key={log.id} 
                    className="border rounded-lg p-4 hover:bg-gray-50 transition-colors"
                    data-testid={`audit-log-entry-${log.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-1">
                          <Badge className={`${actionConfig.color} text-xs font-medium`}>
                            {actionConfig.label}
                          </Badge>
                          {log.dealer_name && (
                            <span className="text-sm font-medium text-gray-700">
                              {log.dealer_name}
                            </span>
                          )}
                        </div>
                        
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">{log.actor_email}</span>
                          {metadataDisplay && (
                            <span className="text-gray-500 ml-2">— {metadataDisplay}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="text-right text-sm text-gray-500 whitespace-nowrap">
                        {formatDate(log.created_at)}
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex items-center justify-between mt-6 pt-4 border-t">
              <Button
                variant="outline"
                size="sm"
                disabled={page === 0}
                onClick={() => setPage(p => p - 1)}
              >
                ← Previous
              </Button>
              
              <span className="text-sm text-gray-600">
                Page {page + 1} of {totalPages}
              </span>
              
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages - 1}
                onClick={() => setPage(p => p + 1)}
              >
                Next →
              </Button>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default AuditLogPage;
